/**
 * @file KeywordInput.tsx
 * @description Component for handling keyword input with tags.
 * Manages keyword input, addition, removal, and state synchronization.
 */

import React, { useState } from "react";

const KeywordInput: React.FC<{
  keywords: string[];
  setKeywords: React.Dispatch<React.SetStateAction<string[]>>;
  placeholder: string;
  onKeyDownCapture?: (
    e: React.KeyboardEvent<HTMLInputElement>,
    hasNewKeyword: boolean
  ) => void;
}> = ({ keywords, setKeywords, placeholder, onKeyDownCapture }) => {
  const [inputValue, setInputValue] = useState("");

  const addKeyword = () => {
    const valueToAdd = inputValue.trim();
    if (valueToAdd && !keywords.includes(valueToAdd)) {
      setKeywords([...keywords, valueToAdd]);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const hasNewKeyword = inputValue.trim().length > 0;
    if ((e.key === "Enter" || e.key === ",") && hasNewKeyword) {
      e.preventDefault();
      addKeyword();
    } else if ((e.key === "Backspace" || e.key === "Delete") && !inputValue) {
      e.preventDefault();
      const keywordsCopy = [...keywords];
      keywordsCopy.pop();
      setKeywords(keywordsCopy);
    }
    if (onKeyDownCapture) {
      onKeyDownCapture(e, hasNewKeyword);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value.replace(/^,|,$/g, ""));
  };

  const handleBlur = () => {
    addKeyword();
  };

  const handleRemoveKeyword = (removedKeyword: string) => {
    setKeywords(keywords.filter((keyword) => keyword !== removedKeyword));
  };

  return (
    <>
      {keywords.map((keyword, index) => (
        <div key={index} className="flex items-center gap-1">
          <div className="flex items-center h-6 px-2 text-white bg-gray-500 rounded-full">
            <span className="mr-2">{keyword}</span>
            <button
              type="button"
              className="flex items-center justify-center text-lg text-white rounded-full"
              onClick={() => handleRemoveKeyword(keyword)}
            >
              &times;
            </button>
          </div>
        </div>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyDownCapture={(e) =>
          onKeyDownCapture && onKeyDownCapture(e, inputValue.trim().length > 0)
        }
        onBlur={handleBlur}
        placeholder={placeholder}
        className="bg-transparent focus:outline-none grow"
      />
    </>
  );
};

export default KeywordInput;
